import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Bell, AlertTriangle, X } from "lucide-react";

export default function StockAlerts() {
  const [data, setData] = useState({ zero: [], low: [], total: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/stock-alerts");
      setData(data);
    } catch {
      /* silencioso — não bloqueia app */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // atualiza a cada 1 minuto
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const count = data.total || 0;
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const resolveImg = (u) =>
    u && u.startsWith("/api/") ? `${BACKEND}${u}` : u;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="stock-alerts-btn"
        className="relative p-2 hover:bg-white/10 rounded-sm text-white transition-colors"
        title={`${count} alerta${count === 1 ? "" : "s"} de estoque`}
      >
        <Bell size={18} />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 bg-red-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center"
            data-testid="stock-alerts-count"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[340px] max-h-[480px] overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-xl z-50 text-slate-800"
          data-testid="stock-alerts-panel"
        >
          <div className="sticky top-0 bg-white border-b border-slate-100 p-3 flex justify-between items-center">
            <div>
              <div className="font-display font-bold text-sm">
                Alertas de Estoque
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                {count} produto{count === 1 ? "" : "s"}
              </div>
            </div>
            <button onClick={() => setOpen(false)}>
              <X size={14} />
            </button>
          </div>

          {count === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              Tudo certo! Nenhum produto esgotado ou em alerta. ✅
            </div>
          )}

          {data.zero?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 bg-red-50 text-red-700 text-[10px] uppercase tracking-wider font-bold border-b border-red-100">
                Esgotados ({data.zero.length})
              </div>
              {data.zero.map((p) => (
                <Link
                  key={p.id}
                  to="/app/products"
                  onClick={() => setOpen(false)}
                  data-testid={`alert-zero-${p.id}`}
                  className="flex items-center gap-2 p-2 border-b border-slate-100 hover:bg-slate-50"
                >
                  {p.image_url ? (
                    <img
                      src={resolveImg(p.image_url)}
                      alt=""
                      className="w-10 h-10 rounded-sm object-cover border border-slate-200"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-slate-100 rounded-sm" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">
                      {p.name}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {p.sku || "—"}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-sm">
                    0 un
                  </span>
                </Link>
              ))}
            </div>
          )}

          {data.low?.length > 0 && (
            <div>
              <div className="px-3 py-1.5 bg-amber-50 text-amber-700 text-[10px] uppercase tracking-wider font-bold border-b border-amber-100 flex items-center gap-1">
                <AlertTriangle size={10} /> Estoque Baixo ({data.low.length})
              </div>
              {data.low.map((p) => (
                <Link
                  key={p.id}
                  to="/app/products"
                  onClick={() => setOpen(false)}
                  data-testid={`alert-low-${p.id}`}
                  className="flex items-center gap-2 p-2 border-b border-slate-100 hover:bg-slate-50"
                >
                  {p.image_url ? (
                    <img
                      src={resolveImg(p.image_url)}
                      alt=""
                      className="w-10 h-10 rounded-sm object-cover border border-slate-200"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-slate-100 rounded-sm" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">
                      {p.name}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {p.sku || "—"}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-sm">
                    {p.stock} un
                  </span>
                </Link>
              ))}
            </div>
          )}

          <div className="p-2 bg-slate-50 text-center border-t border-slate-100">
            <Link
              to="/app/products"
              onClick={() => setOpen(false)}
              className="text-xs text-[#2563EB] hover:underline"
            >
              Ver todo o estoque →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
